import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Utils } from 'src/app/core/resources/utils';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { FreightListService } from '../cargo-list/cargo-list.service';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { Destination } from 'src/app/core/interfaces/destination';
import { Address } from 'src/app/core/interfaces/address';
import { AddressCargo } from 'src/app/core/interfaces/addressCargo';
import { LinkedServiceAddress, LinkedServiceDto } from 'src/app/core/interfaces/linkedServiceDto';
import { DateManager } from 'src/app/core/managers/date.manager';
import { Arrive } from 'src/app/core/managers/cargo.manager';
import { CargoManager } from 'src/app/core/managers/cargo.manager';
import { CargoStateDict } from 'src/app/core/enums/cargoState.enum';
import { ServiceValidationManager } from 'src/app/core/managers/service-validation.manager';
import { ModalEnum } from 'src/app/core/enums/modal.enum';

@Component({
    selector: 'app-link-services',
    templateUrl: './link-services.component.html',
    styleUrls: ['./link-services.component.scss']
})
export class LinkServicesComponent implements OnInit {

    consecutivesControl: FormControl = new FormControl(null);
    step: number = 1;
    cargosSelected: { cargo: Cargo, errorMessage?: string }[] = [];
    vehiclesAllowed: { licensePlate: string, driver: string, id: string, consecutive: number }[] = [];
    vehicleSelected: FormControl = new FormControl('', Validators.required);
    manifestType: { code?: string, description?: string };
    addresses: LinkedServiceAddress[] = [];
    errorOnAddresses: { error: string, index: number } = { error: '', index: -1 };
    middleAddressesAllowed: LinkedServiceAddress[] = [];
    middleAddressControl: FormControl = new FormControl('', Validators.required);

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            cargo: Cargo,
        },
        public dialogRef: MatDialogRef<LinkServicesComponent>,
        public utils: Utils,
        private spinner: NgxSpinnerService,
        private snackBarService: SnackBarService,
        private cargoListService: FreightListService,
        private cargoManager: CargoManager,
        private serviceValidationManager: ServiceValidationManager,
        private dialog: MatDialog
    ) { }

    ngOnInit() {
        this.cargosSelected.push(this.processCargo(this.data.cargo));
    }

    async searchConsecutives() {
        if (!this.consecutivesControl.value) return this.snackBarService.openSnackBar("Ingrese los consecutivos de los servicios", undefined, 'alert');
        const consecutives: string[] = this.consecutivesControl.value.split(',');
        const consecutivesToFind: string[] = consecutives.filter(consecutive => !this.cargosSelected.some(selected => selected && selected.cargo && selected.cargo.consecutive.toString() === consecutive));
        if (!consecutivesToFind.length) return this.snackBarService.openSnackBar("Todos los servicios ya están enlazados", undefined, 'alert');
        if (consecutivesToFind.length + this.cargosSelected.length >= 30) return this.snackBarService.openSnackBar("No se pueden enlazar más de 30 servicios", undefined, 'alert');
        const listCargo = await this.findCargos(consecutivesToFind);
        if (!listCargo || !listCargo.length) return this.snackBarService.openSnackBar("No se encontraron los servicios digitados", undefined, 'alert');
        this.cargosSelected.push(...listCargo.map(cargo => this.processCargo(cargo)));
        const nonExistentList: string[] = consecutivesToFind.filter(consecutive => !listCargo.some(cargo => cargo && cargo.consecutive.toString() === consecutive));
        if (nonExistentList.length > 0) this.snackBarService.openSnackBar(`No se encontraron los servicios ${nonExistentList.join(', ')}`, undefined, 'alert');
        else this.snackBarService.openSnackBar("Servicios encontrados y agregados correctamente", undefined, 'success');
        this.consecutivesControl.setValue('');
    }

    async findCargos(consecutives: string[]): Promise<Cargo[]> {
        try {
            this.spinner.show();
            let filters = '';
            filters += `&states=${encodeURIComponent(JSON.stringify(['Created', 'Accepted', 'Start service']))}`;
            filters += `&consecutives=${encodeURIComponent(JSON.stringify(consecutives))}`;
            const response = await this.cargoListService.getListCargo(filters).toPromise();
            const cargos = response.results;
            this.spinner.hide();
            if (!cargos || !cargos.length) return [];
            return cargos;
        } catch (error) {
            this.spinner.hide();
            return [];
        }
    }

    processCargo(cargo: Cargo): { cargo: Cargo, errorMessage?: string } {
        if (cargo.consecutiveMainCargo)
            return { cargo: cargo, errorMessage: `El servicio ${cargo.consecutive} ya está enlazado a otro servicio` };
        if (cargo.subCargos && cargo.subCargos.length)
            return { cargo: cargo, errorMessage: `El servicio ${cargo.consecutive} ya es un servicio consolidado` };
        if (cargo.manifestAuthorization)
            return { cargo: cargo, errorMessage: `El servicio ${cargo.consecutive} ya tiene un manifiesto generado` };
        const compatibilityError = this.getServiceCompatibilityError(cargo);
        if (compatibilityError) return { cargo: cargo, errorMessage: compatibilityError };
        return { cargo: cargo };
    }

    /**
     * Compatibilidad entre los servicios de un consolidado:
     *
     * - Los de escolta y candado satelital no declaran empresa de transporte, así
     *   que entre ellos se consolidan sin restricción.
     * - Un servicio de transporte no se mezcla con uno de escolta o candado.
     * - Entre servicios de transporte, todos deben declarar la misma empresa.
     *
     * La regla no depende del tipo de SaaS: aplica igual en una de transporte que
     * en una de seguridad. En una de seguridad queda inerte por construcción,
     * porque ninguno de sus servicios llega a la rama de empresa de transporte.
     */
    private getServiceCompatibilityError(cargo: Cargo): string {
        const isTransport = this.utils.isTransportService(cargo);
        if (isTransport !== this.utils.isTransportService(this.data.cargo))
            return `El servicio ${cargo.consecutive} es un servicio de ${this.getServiceTypeName(cargo)} y no se puede consolidar con los demás servicios seleccionados`;
        if (!isTransport) return '';
        const transportCompanyId = this.utils.getNestedValue(cargo, 'transportCompany.id');
        if (!transportCompanyId)
            return `El servicio ${cargo.consecutive} no tiene una empresa de transporte definida`;
        const baseTransportCompanyId = this.utils.getNestedValue(this.data.cargo, 'transportCompany.id');
        if (!baseTransportCompanyId) return '';
        if (transportCompanyId !== baseTransportCompanyId)
            return `El servicio ${cargo.consecutive} pertenece a una empresa de transporte diferente`;
        return '';
    }

    private getServiceTypeName(cargo: Cargo): string {
        if (this.utils.isEscortedService(cargo)) return 'escolta';
        if (this.utils.isSatelliteLockService(cargo)) return 'candado satelital';
        return 'transporte';
    }

    getCargoLastDestination(cargo: Cargo): Destination {
        const destinations: Destination[] = this.utils.getNestedValue(cargo, 'cargoFeature.uploadDownload.destination');
        if (destinations && destinations.length) return destinations[destinations.length - 1];
        return null;
    }

    get requiresMiddleAddress(): boolean {
        return this.manifestType && this.manifestType.code === 'I';
    }

    //Control de Avance
    async nextStep(step: number) {
        if (step === 1) {
            if (this.cargosSelected.length < 2) return this.snackBarService.openSnackBar("Debes seleccionar al menos 2 servicios para enlazar", undefined, 'alert');
            const cargosWithErrors = this.cargosSelected.filter(selected => selected && selected.errorMessage);
            if (cargosWithErrors.length) return this.snackBarService.openSnackBar(cargosWithErrors[0].errorMessage, undefined, 'alert');
            const cargosWithLicensePlate = this.cargosSelected.filter(selected => selected && selected.cargo && selected.cargo.licensePlate && selected.cargo.confirmedDriver);
            if (!cargosWithLicensePlate.length) return this.snackBarService.openSnackBar("Al menos un servicio debe tener vehículo asignado y confirmado", undefined, 'alert');
            const cargosWithMinistry = this.cargosSelected.filter(selected => selected && selected.cargo && selected.cargo.ministry);
            const cargosWithoutMinistry = this.cargosSelected.filter(selected => selected && selected.cargo && !selected.cargo.ministry);
            if (cargosWithMinistry.length && cargosWithoutMinistry.length)
                return this.snackBarService.openSnackBar("Todos los servicios deben requerir manifiesto o no requerirlo", undefined, 'alert');
            const manifestTypeCargos = cargosWithMinistry.map(selected => selected && selected.cargo && selected.cargo.manifestType).filter(manifestType => manifestType && manifestType.description);
            const uniqueManifestTypes = Array.from(new Map(manifestTypeCargos.map(mt => [mt.description, mt])).values());
            if (uniqueManifestTypes.length > 1)
                return this.snackBarService.openSnackBar("Todos los servicios deben tener el mismo tipo de manifiesto", undefined, 'alert');
            this.manifestType = uniqueManifestTypes && uniqueManifestTypes.length ? uniqueManifestTypes[0] : null;
            const uniquePlatesMap = new Map<string, any>();
            cargosWithLicensePlate.forEach(selected => {
                const plate = this.utils.getNestedValue(selected, 'cargo.licensePlate');
                if (!uniquePlatesMap.has(plate)) {
                    uniquePlatesMap.set(plate, {
                        licensePlate: plate,
                        driver: this.utils.getNestedValue(selected, 'cargo.driver'),
                        id: this.utils.getNestedValue(selected, 'cargo.id'),
                        consecutive: this.utils.getNestedValue(selected, 'cargo.consecutive')
                    });
                }
            });
            this.vehiclesAllowed = Array.from(uniquePlatesMap.values());
            if (this.vehiclesAllowed.length === 1) this.vehicleSelected.setValue(this.vehiclesAllowed[0]);
            await this.verifyStepOne();
            this.step += 1;
        } else if (step === 2) {
            this.vehicleSelected.markAsTouched();
            this.middleAddressControl.markAsTouched();
            if (this.utils.errorMessagesCustomized(this.vehicleSelected, 'placa')) return;
            if (this.requiresMiddleAddress && (!this.middleAddressControl.value || !this.middleAddressControl.value.municipalityCode))
                return this.snackBarService.openSnackBar("Debe seleccionar una dirección intermedia", undefined, 'alert');
            this.step += 1;
        }
    }

    backStep() {
        this.step -= 1;
    }

    async verifyStepOne() {
        this.addresses = this.utils.getOrderedAddressesByDate(this.cargosSelected.map(selected => selected.cargo));
        this.middleAddressesAllowed = this.addresses.slice(1, this.addresses.length - 1);
        this.middleAddressControl.setValue(null);
        if (this.requiresMiddleAddress && this.middleAddressesAllowed.length === 1)
            this.middleAddressControl.setValue(this.middleAddressesAllowed[0]);
        this.spinner.show();
        await this.verifyAddresses();
        this.spinner.hide();
    }

    private async verifyAddresses() {
        this.errorOnAddresses = { error: '', index: -1 };
        const arrivals: Arrive[] = []
        this.addresses.forEach(address => arrivals.push({ location: address.address.location, arrive: address.date, address: address.address.address }));
        for (let i = 0; i < arrivals.length - 1; i++) {
            const arrivalA = arrivals[i];
            const arrivalB = arrivals[i + 1];
            if (DateManager.isSame(arrivalA.arrive, arrivalB.arrive, 'seconds') && !this.utils.deepEqual(arrivalA.location, arrivalB.location))
                return this.errorOnAddresses = { error: `La dirección ${arrivalA.address} y la dirección ${arrivalB.address} no pueden tener la misma fecha y hora`, index: i };
        }
        let validAddresses: { valid: boolean, min: number, address: string, index: number };
        try {
            validAddresses = await this.cargoManager.validateTripTimes(arrivals).toPromise();
        } catch (error) {
            return this.errorOnAddresses = { error: "Ocurrió un error al verificar las direcciones", index: -1 };
        }
        if (!validAddresses)
            return this.errorOnAddresses = { error: "Ocurrió un error al verificar las direcciones", index: -1 };
        if (!validAddresses.valid) {
            let message = 'Los tiempos estimados entre las direcciones son imposibles de cumplir.';
            if (validAddresses && validAddresses.min && validAddresses.address) {
                message = `Para llegar a la dirección ${validAddresses.address} se requieren al menos `;
                const duration = DateManager.durationFormat(validAddresses.min, 'seconds');
                if (duration.hours) message += `${duration.hours} horas `;
                if (duration.minutes) message += `${duration.minutes} minutos `;
                if (duration.seconds) message += `${duration.seconds} segundos `;
            }
            return this.errorOnAddresses = { error: message, index: validAddresses.index };
        }
    }

    async editSection(cargoId: string, section: 'origin' | 'destination') {
        const cargo: Cargo = this.cargosSelected.find(cargo => cargo.cargo.id === cargoId).cargo;
        const sections = this.serviceValidationManager.getSectionsWithValidation(cargo);
        if (!sections[section])
            return this.snackBarService.openSnackBar(`No es posible editar la sección`, undefined, 'error');
        for (const canEdit of sections[section].canEdit) {
            if (!canEdit.restriction)
                return this.snackBarService.openSnackBar(canEdit.message, undefined, 'error');
        }
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
            cargo: cargo,
            ...(sections[section].dialogData || {})
        };
        dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
        dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
        dialogConfig.width = ModalEnum.LARGE_WIDTH;
        dialogConfig.autoFocus = false;
        const result = await this.dialog.open(sections[section].component, dialogConfig).afterClosed().toPromise();
        if (!result || !result.state) return;
        const newCargo: Cargo[] = await this.findCargos([cargo.consecutive.toString()]);
        if (!newCargo || !newCargo.length)
            return this.snackBarService.openSnackBar("No fue posible refrescar el servicio", undefined, 'error');
        this.cargosSelected = this.cargosSelected.map(cargo => cargo.cargo.id === cargoId ? this.processCargo(newCargo[0]) : cargo);
        if (this.cargosSelected.some(cargo => !!cargo.errorMessage)) {
            this.snackBarService.openSnackBar(this.cargosSelected.find(cargo => !!cargo.errorMessage).errorMessage, undefined, 'alert');
            this.step = 1;
            return;
        }
        await this.verifyStepOne();
        if (this.requiresMiddleAddress && (!this.middleAddressControl.value || !this.middleAddressControl.value.municipalityCode)) {
            this.snackBarService.openSnackBar("Debe seleccionar una dirección intermedia", undefined, 'alert');
            this.step = 2;
            return;
        }
    }

    onSubmit() {
        if (this.errorOnAddresses.error) return this.snackBarService.openSnackBar(this.errorOnAddresses.error, undefined, 'alert');
        if (this.requiresMiddleAddress && (!this.middleAddressControl.value || !this.middleAddressControl.value.municipalityCode)) {
            this.snackBarService.openSnackBar("Debe seleccionar una dirección intermedia", undefined, 'alert');
            this.step = 2;
            return;
        }
        const middleAddress: LinkedServiceAddress = this.middleAddressControl.value;
        const data: LinkedServiceDto = {
            childCargoIds: this.cargosSelected.map(cargo => cargo.cargo.id),
            sourceId: this.vehicleSelected.value.id,
            dateLoad: DateManager.dateToString(this.addresses[0].date),
            origin: {
                name: this.addresses[0].city,
                municipalityCode: this.addresses[0].municipalityCode,
                addresses: [{ address: this.addresses[0].address.address }]
            },
            destination: {
                name: this.addresses[this.addresses.length - 1].city,
                municipalityCode: this.addresses[this.addresses.length - 1].municipalityCode,
                addresses: [{ address: this.addresses[this.addresses.length - 1].address.address }],
                downloadDate: DateManager.dateToString(this.addresses[this.addresses.length - 1].date),
            },
            intermediate: middleAddress ? {
                name: middleAddress.city,
                municipalityCode: middleAddress.municipalityCode,
                addresses: [{ address: middleAddress.address.address }]
            } : null,
        }
        this.spinner.show();
        this.cargoListService.linkServices(data).subscribe(
            (response) => {
                this.spinner.hide();
                this.snackBarService.openSnackBar("El servicio consolidado fue creado exitosamente", undefined, 'success');
                this.dialogRef.close({ state: true });
            }, (error) => {
                this.spinner.hide();
                this.snackBarService.openSnackBar("Ocurrió un error al crear el servicio consolidado", undefined, 'error');
            }
        );
    }

    getCargoState(cargo: Cargo): string {
        if (!cargo) return '';
        return CargoStateDict[cargo.state ? cargo.state : cargo.requestState ? cargo.requestState : ''];
    }

    removeCargo(index: number) {
        this.cargosSelected.splice(index, 1);
    }
}
